
import { Link, useLocation } from 'react-router-dom';
import { 
  Sidebar, 
  SidebarContent, 
  SidebarFooter, 
  SidebarHeader, 
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
  SidebarSeparator
} from "@/components/ui/sidebar";
import { useLanguage } from '@/contexts/LanguageContext';
import { Film, Layers, Grid2x2, Clapperboard, Users, Pencil, Menu, Settings, User, Home, Shield, BarChart, Star } from 'lucide-react';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { Footer } from './Footer';
import { SidebarUserSection } from './SidebarUserSection';
import { SidebarMenuGroup } from './SidebarMenuGroup';
import { Navbar } from './Navbar';
import { Separator } from '@/components/ui/separator';

export const MainLayout = ({ children, sidebarDefaultOpen }: { children: React.ReactNode, sidebarDefaultOpen?: boolean }) => {
  // Fix the import from react-router to react-router-dom
  const { user, isAuthenticated, isAdmin, isCreator } = useAuth();
  const { theme } = useTheme();
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const defaultSidebarOpen = sidebarDefaultOpen !== undefined ? sidebarDefaultOpen : !isHomePage;

  const browseLicensedContentMenu = [
    { path: '/channels', labelKey: 'sidebar.channels', icon: Film },
    { path: '/playlists', labelKey: 'sidebar.playlists', icon: Layers },
    { path: '/clips', labelKey: 'sidebar.clips', icon: Grid2x2 }
  ];

  const creatorHubMenu = [
    { path: '/derivatives', labelKey: 'sidebar.derivatives', icon: Clapperboard },
    { path: '/creators', labelKey: 'sidebar.creators', icon: Users }
  ];
  
  const creatorSpaceMenu = [
    { path: '/dashboard', labelKey: 'sidebar.dashboard', icon: Home },
    { path: '/create-derivative', labelKey: 'sidebar.publishCreation', icon: Pencil },
    { path: '/creator/panel', state: { activeTab: 'favorites' }, labelKey: 'sidebar.myFavorites', icon: Star }
  ];
  
  const adminDashboardMenu = [
    { path: '/admin?tab=channels', labelKey: 'sidebar.manageChannels', icon: Film },
    { path: '/admin?tab=users', labelKey: 'sidebar.manageUsers', icon: Users },
    { path: '/admin?tab=licenses', labelKey: 'sidebar.manageLicenses', icon: Settings },
    { path: '/admin?tab=derivatives', labelKey: 'sidebar.manageDerivatives', icon: Shield },
    { path: '/admin?tab=challenges', labelKey: 'sidebar.manageChallenges', icon: Clapperboard },
    { path: '/admin?tab=analytics', labelKey: 'sidebar.analytics', icon: BarChart }
  ];

  return (
    <SidebarProvider defaultOpen={defaultSidebarOpen}>
      <div className="flex min-h-screen w-full relative overflow-hidden">
        {isMobile && <Navbar />}
        
        {!isMobile && (
          <SidebarTrigger className="fixed top-6 left-4 z-[60] bg-primary/10 shadow-lg rounded-md border border-border/40 flex items-center justify-center p-2.5 hover:bg-accent transition-colors">
            <Menu className="h-5 w-5" />
          </SidebarTrigger>
        )}
        
        <Sidebar className="border-r border-border/50">
          <SidebarHeader className="bg-card">
            <div className="flex items-center justify-center py-4">
              <Link to="/" className="flex items-center transition-transform hover:scale-105">
                <div className="px-4 py-1">
                  <img
                    src={theme === 'dark' ? '/lovable-uploads/a0f0939a-3b33-4cf7-8f8d-85504e9ad117.png' : '/lovable-uploads/ee1ae56d-3fb6-45ea-9a24-8cfa75082425.png'}
                    alt="Relut"
                    className="h-9 w-auto object-contain"
                  />
                </div>
              </Link>
            </div>
          </SidebarHeader>
          
          <SidebarContent className="bg-card">
            <SidebarMenuGroup labelKey="sidebar.browseLicensedContent" items={browseLicensedContentMenu} />
            <SidebarMenuGroup labelKey="sidebar.creatorHub" items={creatorHubMenu} />
            
            {isAuthenticated && (
              <>
                {isAdmin ? (
                  <SidebarMenuGroup labelKey="sidebar.adminSpace" items={adminDashboardMenu} />
                ) : (
                  <SidebarMenuGroup labelKey="sidebar.mySpace" items={creatorSpaceMenu} />
                )}
              </>
            )}
          </SidebarContent>
          
          <SidebarFooter className="bg-card">
            <div className="p-2">
              <div className="flex items-center justify-between mb-2 p-2 rounded-md bg-background/50 border border-border/40">
                <div className="w-1/2 flex items-center justify-center">
                  <ThemeToggle />
                </div>
                <Separator orientation="vertical" className="h-8 mx-1" />
                <div className="w-1/2 flex items-center justify-center">
                  <LanguageToggle />
                </div>
              </div>
              <SidebarUserSection />
            </div>
          </SidebarFooter>
        </Sidebar>
        
        <SidebarInset className="w-full flex-shrink-0 overflow-x-hidden">
          <div className={`${isMobile ? "pt-16" : ""} flex flex-col min-h-screen w-full overflow-x-hidden`}>
            <main className="flex-grow overflow-x-hidden">
              {children}
            </main>
            <Footer />
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};
